import { useState, useEffect } from 'react';
import StatusBadge from '../../../components/common/StatusBadge';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import InterfaceDependencyWarning, {
  type InterfaceDependencyReport,
} from '../../../components/edges/InterfaceDependencyWarning';
import edgeService from '../../../services/edge.service';
import { useTenantStore } from '../../../stores/tenantStore';
import { useToast } from '../../../components/common/ToastContainer';

interface PhysicalInterfacesConfigProps {
  edgeId: string;
}

/**
 * Natural alphanumeric sort so G0, G1, G2, …, G10 render in port order.
 * Applied on every read of the interface list — initial fetch and every
 * re-fetch after a toggle — so the order never drifts (VSNP-658).
 */
const sortInterfacesByName = <T extends { name?: string | null }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }),
  );

const PhysicalInterfacesConfig = ({ edgeId }: PhysicalInterfacesConfigProps) => {
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [physicalInterfaces, setPhysicalInterfaces] = useState<any[]>([]);
  const [vppStatus, setVppStatus] = useState<any>(null);
  // VSNP-700: track VPP-status load separately from the table-data load
  // so we can render the table immediately from DB rows and surface a
  // "live telemetry unavailable" hint only AFTER we've actually tried.
  const [vppStatusLoaded, setVppStatusLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  // Dependency warning state
  const [toggleConfirm, setToggleConfirm] = useState<{
    interfaceId: string;
    name: string;
    currentState: boolean;
    report: InterfaceDependencyReport | null;
  } | null>(null);
  const [loadingDeps, setLoadingDeps] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // VSNP-700: split the page load. The DB-backed list is fast and
    // drives the table — render it the moment it arrives. The VPP
    // telemetry decoration (operational state, link speed, MAC) is
    // best-effort and runs in parallel; it never gates the table.
    // Previously a single Promise.all gated everything on whichever
    // query was slowest, and for offline edges that combined latency
    // (plus payload-parse cost on a large status_payload) was the
    // user-visible "slow tables" complaint.
    const fetchInterfaces = async () => {
      try {
        setLoading(true);
        const physical = await edgeService.getPhysicalInterfaces(edgeId);
        if (cancelled) return;
        setPhysicalInterfaces(sortInterfacesByName(physical.items || []));
      } catch (err) {
        console.error('Failed to load physical interfaces:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const fetchVppStatus = async () => {
      try {
        const data = await edgeService.getLatestStatus(edgeId);
        if (cancelled) return;
        setVppStatus(data);
      } catch {
        // Non-fatal: table falls back to DB-cached values per row.
      } finally {
        if (!cancelled) setVppStatusLoaded(true);
      }
    };

    fetchInterfaces();
    fetchVppStatus();

    return () => {
      cancelled = true;
    };
  }, [edgeId, selectedTenantId]);

  // Surfaced above the table when the live-telemetry fetch finished but
  // returned nothing — typically an offline edge that has never sent an
  // inform, or a stale-database edge after a state-DB wipe.
  const showOfflineNotice = vppStatusLoaded && !vppStatus;

  // Get VPP interface data by name
  const getVPPInterfaceData = (name: string) => {
    if (!vppStatus?.vpp?.interfaces) return null;
    return vppStatus.vpp.interfaces.find((iface: any) => iface.name === name);
  };

  const handleToggle = async (interfaceId: string, name: string, currentState: boolean) => {
    // If enabling, proceed directly (no dependency risk)
    if (!currentState) {
      await executeToggle(interfaceId, currentState);
      return;
    }

    // If disabling, check dependencies first
    setLoadingDeps(true);
    setToggleConfirm({ interfaceId, name, currentState, report: null });

    try {
      const report = await edgeService.getPhysicalInterfaceDependencies(edgeId, interfaceId);
      const totalDeps = (report.summary?.total || 0) +
        (report.cascadingDependencies?.reduce(
          (sum: number, c: any) => sum + (c.dependencies?.length || 0), 0
        ) || 0);

      if (totalDeps === 0) {
        // No dependencies, proceed directly
        setToggleConfirm(null);
        await executeToggle(interfaceId, currentState);
      } else {
        // Show warning dialog
        setToggleConfirm({ interfaceId, name, currentState, report });
      }
    } catch (err: any) {
      setToggleConfirm(null);
      toast.error('Dependency Check Failed', err.message);
    } finally {
      setLoadingDeps(false);
    }
  };

  const executeToggle = async (interfaceId: string, currentState: boolean) => {
    try {
      await edgeService.togglePhysicalInterface(edgeId, interfaceId, !currentState);
      const response = await edgeService.getPhysicalInterfaces(edgeId);
      setPhysicalInterfaces(sortInterfacesByName(response.items || []));
    } catch (err: any) {
      toast.error('Toggle Failed', `Failed to toggle interface: ${err.message}`);
    }
  };

  const confirmToggle = async () => {
    if (!toggleConfirm) return;
    setToggleConfirm(null);
    await executeToggle(toggleConfirm.interfaceId, toggleConfirm.currentState);
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const result = await edgeService.reconcilePhysicalInterfaces(edgeId);
      const inserted = result?.data?.inserted ?? result?.inserted ?? 0;
      const informAt = result?.data?.informAt ?? result?.informAt ?? null;
      if (informAt === null) {
        toast.warning(
          'No VPP Status Yet',
          'Edge has not sent an inform message yet — try again after the agent connects.',
        );
      } else if (inserted === 0) {
        toast.info('Already In Sync', 'No new physical interfaces detected from VPP.');
      } else {
        toast.success(
          'Reconciled',
          `Added ${inserted} physical interface${inserted === 1 ? '' : 's'} from VPP.`,
        );
      }
      const response = await edgeService.getPhysicalInterfaces(edgeId);
      setPhysicalInterfaces(sortInterfacesByName(response.items || []));
    } catch (err: any) {
      toast.error('Reconcile Failed', err?.message || 'Failed to reconcile from VPP');
    } finally {
      setReconciling(false);
    }
  };


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Physical Interfaces</h3>
          <p className="text-sm text-gray-600 mt-1">Configure physical ports and enable/disable interfaces</p>
        </div>
        <button
          type="button"
          onClick={handleReconcile}
          disabled={reconciling}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Backfill any physical NICs the edge has reported via VPP but the orchestrator is missing"
        >
          {reconciling ? 'Reconciling…' : 'Reconcile from VPP'}
        </button>
      </div>

      {/* VSNP-700: live-telemetry probe finished with nothing — show the
          table from DB and tell the operator why the status-dependent
          columns are placeholders. */}
      {showOfflineNotice && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Live telemetry unavailable — values shown are from the last known good state. Operational state, link speed and MAC will update once the edge reconnects.
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Configured State
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Operational State
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Speed</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MAC Address</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Loading interfaces...
                </td>
              </tr>
            ) : physicalInterfaces.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No physical interfaces found
                </td>
              </tr>
            ) : (
              physicalInterfaces.map((iface) => {
                const vppData = getVPPInterfaceData(iface.name);
                const macAddress = vppData?.mac_address || iface.macAddress || '\u2014';
                const operationalState = vppData?.state || iface.operationalState || 'unknown';
                let speed = '\u2014';
                if (vppData?.link_speed && vppData.link_speed > 0) {
                  const kbps = vppData.link_speed;
                  if (kbps >= 1000000) {
                    speed = `${(kbps / 1000000).toFixed(0)} Gbps`;
                  } else if (kbps >= 1000) {
                    speed = `${(kbps / 1000).toFixed(0)} Mbps`;
                  } else {
                    speed = `${kbps} kbps`;
                  }
                } else if (iface.speed) {
                  speed = iface.speed;
                }

                return (
                  <tr key={iface.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {iface.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {vppData?.type || iface.type || 'Ethernet'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={iface.configuredState}
                          onChange={() => handleToggle(iface.id, iface.name, iface.configuredState)}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={operationalState} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{speed}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {macAddress}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Dependency Warning Dialog */}
      <ConfirmDialog
        isOpen={!!toggleConfirm}
        onClose={() => setToggleConfirm(null)}
        onConfirm={confirmToggle}
        title={`Disable Interface "${toggleConfirm?.name || ''}"`}
        message={
          <InterfaceDependencyWarning
            report={toggleConfirm?.report || null}
            loading={loadingDeps}
            action="disable"
          />
        }
        confirmText="Disable Anyway"
        variant="warning"
        isLoading={loadingDeps}
        size="lg"
      />
    </div>
  );
};

export default PhysicalInterfacesConfig;
